import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Server, Braces, Smartphone, ShieldCheck, GitBranch, Sparkles, CalendarClock, ArrowDown } from "lucide-react";
import { useI18n } from "@/lib/i18nContext";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import FeaturedProjects from "@/components/FeaturedProjects";
import ServicesCarousel from "@/components/ServicesCarousel";

const ICON_MAP = { Server, Braces, Smartphone, ShieldCheck, GitBranch, Sparkles };

export default function ServicesPage() {
  const { t, tl, pathWithLang } = useI18n();
  const [services, setServices] = useState([]);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
    api.get("/skills").then((r) => setSkills(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="services-page">
      <Seo title={t("seo.services.title")} description={t("seo.services.description")} />
      {/* HERO */}
      <section className="relative noise overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <div className="absolute inset-0 bg-code-pattern" style={{ backgroundImage: "url(/visuals/code-pattern.png)" }} />
          <div className="absolute -top-40 right-0 w-[45vw] h-[45vw] rounded-full bg-[var(--gold)] opacity-[0.05] blur-[140px]" />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-12 relative z-10">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-5">
            · {t("services.hero.label")}
          </div>
          <h1 className="font-display text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.05] tracking-[-0.035em] max-w-5xl">
            {t("services.hero.title")}{" "}
            <span className="font-serif gold-grad">{t("services.hero.accent")}</span>.
          </h1>
          <p className="mt-8 text-lg text-[var(--text-1)] max-w-3xl leading-relaxed">
            {t("services.hero.intro")}
          </p>
          {/* Jump-to-tech-stack anchor */}
          <Link
            to={pathWithLang("/dienste") + "#skills"}
            data-testid="jump-to-stack"
            className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-[var(--gold)] hover:text-[var(--text-0)] transition-colors group"
          >
            <ArrowDown
              size={14}
              className="transition-transform duration-200 group-hover:translate-y-0.5"
            />
            <span>{t("services.jumpToStack")}</span>
          </Link>
        </div>
      </section>

      {/* SERVICES — compact carousel */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 lg:py-16">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-3">
            · {t("services.label")}
          </div>
          <h2 className="font-display text-3xl lg:text-4xl leading-[1.05] tracking-tight max-w-3xl">
            {t("services.title")} <span className="font-serif gold-grad">{t("services.titleAccent")}</span> {t("services.titleEnd")}
          </h2>
        </div>
        {services.length > 0 && <ServicesCarousel services={services} iconMap={ICON_MAP} />}
      </section>

      {/* FEATURED AI / DATA PROJECTS — sourced from Great Learning e-portfolio */}
      <FeaturedProjects />

      {/* SKILLS MATRIX — tech stack */}
      <section id="skills" className="border-y hairline bg-[var(--bg-1)]/40 scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
          <div className="mb-12">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
              · {t("services.stackLabel")}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight max-w-3xl">
              {t("services.stackHeading")} <span className="font-serif gold-grad">{t("services.stackAccent")}</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {skills.map((cat, idx) => (
              <div key={idx} className="svc-card">
                <div className="text-xs uppercase tracking-wider text-[var(--gold)] mb-5 font-medium">
                  {tl(cat.category)}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {cat.items?.map((it) => (
                    <span
                      key={it.name}
                      className="tag-pill"
                      title={`Level ${"●".repeat(it.level || 1)}${it.years ? ` · ${it.years}` : ""}`}
                    >
                      {it.name}{" "}
                      <span className="text-[var(--gold)]">{"●".repeat(it.level || 1)}</span>
                      {it.years ? (
                        <span className="text-[var(--text-2)] ml-1">·&nbsp;{it.years}J</span>
                      ) : null}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 font-mono text-xs text-[var(--text-2)]">
            {t("services.legend")}<span className="text-[var(--gold)]">XJ</span> {t("services.legendYears")}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <div className="border hairline-gold rounded p-10 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-gradient-to-br from-[var(--bg-1)] to-[var(--bg-0)]">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl lg:text-5xl leading-[1.1] tracking-tight">
              {t("services.matchTitle")} <span className="font-serif gold-grad">{t("services.matchAccent")}</span>{t("services.matchEnd")}
            </h2>
            <p className="text-[var(--text-1)] mt-4 max-w-xl leading-relaxed">
              {t("services.matchDesc")}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
            <Link to={pathWithLang("/kontakt")} data-testid="services-book-btn" className="btn-primary">
              <CalendarClock size={16} /> {t("common.bookCall")}
            </Link>
            <Link to={pathWithLang("/kontakt")} data-testid="services-contact-btn" className="btn-ghost text-xs">
              {t("common.sendMessage")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
